/**
 * TanStack Query hooks for sessions
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sessionsApi } from '@/services/api';
import { queryKeys } from '@/config/queryClient';
import { useLanguage } from '@/store/languageStore';
import { useIsAuthenticated } from '@/store/authStore';
import type { Language, LocalizedSession } from '@/types';

/**
 * Hook to fetch all sessions with optional filtering
 * Automatically uses the current language from the store
 */
export function useSessions(filters?: { category?: string; search?: string }) {
  const language = useLanguage();
  return useQuery({
    queryKey: queryKeys.sessions.list({ ...filters, lang: language }),
    queryFn: () => sessionsApi.getSessions({ ...filters, lang: language }),
  });
}

/**
 * Hook to fetch the published Akustične sessions (A4), localized in the current language.
 * The plain session list is guided only, so these never appear in Vođene or Početna.
 */
export function useAcousticSessions() {
  const language = useLanguage();
  const filters = { session_type: 'acoustic' as const, lang: language };
  return useQuery({
    queryKey: queryKeys.sessions.list(filters),
    queryFn: () => sessionsApi.getSessions(filters) as Promise<LocalizedSession[]>,
  });
}

/**
 * Hook to fetch all sessions with a specific language override
 */
export function useSessionsWithLang(
  lang: Language,
  filters?: { category?: string; search?: string }
) {
  return useQuery({
    queryKey: queryKeys.sessions.list({ ...filters, lang }),
    queryFn: () => sessionsApi.getSessions({ ...filters, lang }),
  });
}

/**
 * Hook to fetch a single session
 * Automatically uses the current language from the store
 */
export function useSession(sessionId: string) {
  const language = useLanguage();
  return useQuery({
    queryKey: queryKeys.sessions.detail(sessionId, language),
    queryFn: () => sessionsApi.getSession(sessionId, language),
    enabled: !!sessionId,
  });
}

/**
 * Hook to fetch session categories
 */
export function useCategories() {
  return useQuery({
    queryKey: queryKeys.sessions.categories(),
    queryFn: () => sessionsApi.getCategories(),
  });
}

/**
 * Hook to fetch the Vođene rows (session groups), named in the current language
 */
export function useSessionGroups() {
  const language = useLanguage();
  return useQuery({
    queryKey: queryKeys.sessions.groups(language),
    queryFn: () => sessionsApi.getGroups(language),
  });
}

/**
 * Hook to fetch session history
 * Automatically uses the current language from the store
 */
export function useSessionHistory() {
  const language = useLanguage();
  return useQuery({
    queryKey: queryKeys.sessions.history(language),
    queryFn: () => sessionsApi.getHistory(language),
  });
}

/**
 * Hook to fetch session statistics
 */
export function useSessionStatistics() {
  return useQuery({
    queryKey: queryKeys.sessions.statistics(),
    queryFn: () => sessionsApi.getStatistics(),
  });
}

/**
 * Hook to fetch recent sessions
 */
export function useRecentSessions() {
  return useQuery({
    queryKey: queryKeys.sessions.recent(),
    queryFn: () => sessionsApi.getRecentSessions(),
  });
}

/**
 * Hook to fetch favorite sessions (signed-in users only)
 * Automatically uses the current language from the store
 */
export function useFavorites() {
  const language = useLanguage();
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: queryKeys.sessions.favorites(language),
    queryFn: () => sessionsApi.getFavorites(language),
    // Favorites belong to a user: signed-out visitors browse public pages without this call.
    enabled: isAuthenticated,
  });
}

/**
 * Hook to fetch session progress
 */
export function useSessionProgress(sessionId: string) {
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: queryKeys.sessions.progress(sessionId),
    queryFn: () => sessionsApi.getProgress(sessionId),
    enabled: !!sessionId && isAuthenticated,
  });
}

/**
 * Hook to start a session
 */
export function useStartSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => sessionsApi.startSession(sessionId),
    onSuccess: (_data: unknown, sessionId: string) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.progress(sessionId) });
      // Invalidate all history queries (any language)
      queryClient.invalidateQueries({ queryKey: [...queryKeys.sessions.all, 'history'] });
    },
  });
}

/**
 * Hook to update session progress
 */
export function useUpdateProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, progress }: { sessionId: string; progress: number }) =>
      sessionsApi.updateProgress(sessionId, progress),
    onSuccess: (_data: unknown, { sessionId }: { sessionId: string; progress: number }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.progress(sessionId) });
    },
  });
}

/**
 * Hook to complete a session
 */
export function useCompleteSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => sessionsApi.completeSession(sessionId),
    onSuccess: (_data: unknown, sessionId: string) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.progress(sessionId) });
      // Invalidate all history queries (any language)
      queryClient.invalidateQueries({ queryKey: [...queryKeys.sessions.all, 'history'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.statistics() });
    },
  });
}

/**
 * Hook to toggle favorite status
 */
export function useToggleFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => sessionsApi.toggleFavorite(sessionId),
    onSuccess: () => {
      // Invalidate all favorites queries (any language)
      queryClient.invalidateQueries({ queryKey: [...queryKeys.sessions.all, 'favorites'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions.lists() });
    },
  });
}
